import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, LogIn } from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import logo from '@/assets/logo.png';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { usePublicSubscriptionStatus } from '@/hooks/usePublicSubscriptionStatus';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { t, language, toggleLanguage } = useLanguage();
  const { branding } = useSiteSettings();
  const { hasCateringAccess } = usePublicSubscriptionStatus();

  const logoUrl = branding.logo_url || logo;

  const allNavItems = [
    { path: '/', label: t('home') },
    { path: '/menu', label: t('menu') },
    { path: '/locations', label: t('locations') },
    { path: '/about', label: t('about') },
    ...(hasCateringAccess ? [{ path: '/catering', label: 'Catering' }] : []),
    { path: '/contact', label: t('contact') },
    { path: '/blog', label: t('blog') },
  ];

  const navItems = allNavItems;

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-card">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src={logoUrl} alt="Junnior's Tacos" className="h-12 md:h-16 w-auto" width="85" height="48" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-3">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-4 py-2 rounded-lg transition-colors font-medium uppercase text-sm ${
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Button
              variant="ghost"
              onClick={toggleLanguage}
              className="ml-8 h-14 w-16 rounded-lg p-0 flex items-center justify-center hover:bg-muted"
              title={`Switch to ${language === 'en' ? 'Spanish' : 'English'}`}
            >
              <div className="flex flex-col items-center justify-center gap-0.5">
                <Globe className="h-5 w-5" />
                <span className="text-[10px] font-medium">{language === 'en' ? 'Español' : 'English'}</span>
              </div>
            </Button>
            <Button
              variant="outline"
              size="sm"
              asChild
              className="ml-4"
            >
              <Link to="/management">
                <LogIn className="h-4 w-4 mr-2" />
                {t('login')}
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              aria-label={`Switch to ${language === 'en' ? 'Spanish' : 'English'}`}
            >
              <Globe className="h-5 w-5" />
            </Button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-lg hover:bg-muted"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden pb-4">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-lg transition-colors font-medium uppercase text-sm ${
                  isActive(item.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/management"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-3 mt-2"
            >
              <Button variant="outline" size="sm" className="w-full">
                <LogIn className="h-4 w-4 mr-2" />
                {t('login')}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
